import { AppBar, Button, Collapse, styled } from '@mui/material';
import { ComponentProps, ComponentType, FC, ReactNode, useCallback, useState, PropsWithChildren } from 'react';

export interface ICollapsiblePanelComponentProps {
    CollapsiblePanelProps: {
        expanded: boolean;
        toggle: () => void;
        setExpanded: (expanded: boolean) => void;
    }
}

export type CollapsiblePanelComponentType<T extends PropsWithChildren<ICollapsiblePanelComponentProps> = any> = ComponentType<T>

export interface ICollapsiblePanelProps<TComponent extends CollapsiblePanelComponentType = any> {
    component?: TComponent;
    ComponentProps?: Omit<ComponentProps<TComponent>, 'CollapsiblePanelProps'>;
}

export interface IDefaultCollapsiblePanelComponent extends ICollapsiblePanelComponentProps {
    title?: ReactNode;
}

const CollapsiblePanelTitle = styled(Button)(({ theme }) => ({
    textTransform: 'none',
    justifyContent: 'flex-start',
    borderRadius: 0
}));

export const DefaultCollapsiblePanelComponent: FC<PropsWithChildren<IDefaultCollapsiblePanelComponent>> = ({
    CollapsiblePanelProps: {
        expanded,
        toggle
    },
    children,
    title
}) => {
    return (
        <>
            <CollapsiblePanelTitle 
                variant='contained' 
                color="secondary"
                fullWidth
                onClick={toggle}
            >
                {title ?? 'Toggle'}
            </CollapsiblePanelTitle>
            {children}
        </>
    )
};

const CollapsiblePanel = <TComponent extends CollapsiblePanelComponentType = typeof DefaultCollapsiblePanelComponent>({
    component,
    ComponentProps,
    children
}: PropsWithChildren<ICollapsiblePanelProps<TComponent>>) => {
    const [expanded, setExpanded] = useState(false);

    const toggleExpanded = useCallback(() => {
        setExpanded(c => !c);
    }, [setExpanded])

    const Component = component ?? DefaultCollapsiblePanelComponent;

    return (
        <Component
            {...ComponentProps}
            CollapsiblePanelProps={{
                expanded: expanded,
                toggle: toggleExpanded,
                setExpanded: setExpanded
            }}
        >
            <Collapse in={expanded} mountOnEnter unmountOnExit>
                {children}
            </Collapse>
        </Component>
    )
}

export default CollapsiblePanel;